import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  FileText, Users, Calculator, Eye, Building, Building2,
  LogOut, Menu, X, Lock, School, ChevronRight, Home
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', icon: Home, label: 'Dashboard' },
  { path: '/dashboard/blueprint', icon: FileText, label: 'Blueprint Builder' },
  { path: '/dashboard/students', icon: Users, label: 'Students' },
  { path: '/dashboard/marks', icon: Calculator, label: 'Marks Entry' },
  { path: '/dashboard/preview', icon: Eye, label: 'Preview & Export' },
  { path: '/dashboard/cluster', icon: Building, label: 'Cluster Report' },
  { path: '/dashboard/block', icon: Building2, label: 'Block Report' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-40 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-navy">LO Generator</span>
        </div>
        <div className="w-10" />
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-slate-200 z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-200">
            <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-navy">Teacher Utility</span>
          </div>

          {/* School Info (Locked) */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 mb-2">
              <School className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-medium text-slate-500 uppercase">School Info</span>
              <Lock className="w-3 h-3 text-slate-400" />
            </div>
            <div className="locked-field rounded-lg p-3 text-sm">
              <p className="font-medium text-navy truncate" title={user.school_name}>
                {user.school_name}
              </p>
              <p className="text-slate-500 text-xs mt-1">UDISE: {user.udise_code}</p>
              <p className="text-slate-500 text-xs">{user.cluster}, {user.block}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-navy text-white' 
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-200">
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
